import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage venue interest functionality
 * @param {Object} params - Hook parameters
 * @param {string} params.venueId - The venue ID
 * @param {string} params.customerToken - Customer authentication token
 * @param {boolean} params.isPublicMode - Whether user is in public mode
 * @param {Function} params.triggerLogin - Function to trigger login modal
 * @param {Function} params.showToast - Function to show toast notifications
 * @param {string} params.apiUrl - API base URL
 * @returns {Object} Interest state and handlers
 */
export function useVenueInterest({
  venueId,
  customerToken,
  isPublicMode,
  triggerLogin,
  showToast,
  apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000"
}) {
  const [isInterested, setIsInterested] = useState(false);
  const [interestedLoading, setInterestedLoading] = useState(false);

  // Check if customer is interested in this venue
  useEffect(() => {
    const checkInterest = async () => {
      if (!customerToken || isPublicMode || !venueId) return;

      try {
        const response = await fetch(
          `${apiUrl}/loyalty/interest/venue/${venueId}/check?customer_token=${encodeURIComponent(customerToken)}`,
          { headers: { "Content-Type": "application/json" } }
        );
        if (response.ok) {
          const data = await response.json();
          setIsInterested(data.is_interested);
        }
      } catch (error) {
        console.error("Error checking venue interest:", error);
      }
    };

    checkInterest();
  }, [apiUrl, customerToken, isPublicMode, venueId]);

  // Auto-trigger interest after successful login if there was a pending action
  useEffect(() => {
    const autoExpressInterest = async () => {
      if (isPublicMode || !customerToken || !venueId) return;

      const pendingVenueId = sessionStorage.getItem("pending_interest_venue_id");
      if (!pendingVenueId || pendingVenueId !== venueId) return;

      sessionStorage.removeItem("pending_interest_venue_id");
      await new Promise(resolve => setTimeout(resolve, 500));

      try {
        const checkResponse = await fetch(
          `${apiUrl}/loyalty/interest/venue/${venueId}/check?customer_token=${encodeURIComponent(customerToken)}`,
          { headers: { "Content-Type": "application/json" } }
        );

        if (checkResponse.ok) {
          const data = await checkResponse.json();
          if (data.is_interested) {
            showToast("You're already interested in this venue!", "info");
            return;
          }
        }

        const response = await fetch(`${apiUrl}/loyalty/interest/venue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_token: customerToken,
            venue_id: parseInt(venueId, 10),
          }),
        });

        if (response.ok) {
          await response.json();
          setIsInterested(true);
          showToast("Great! We've recorded your interest. We'll work on bringing discounts here!", "success");
        }
      } catch (error) {
        console.error("Error auto-expressing interest:", error);
      }
    };

    autoExpressInterest();
  }, [apiUrl, customerToken, isPublicMode, venueId, showToast]);

  // Handle interest toggle
  const handleInterestToggle = useCallback(async () => {
    if (isPublicMode) {
      sessionStorage.setItem("pending_interest_venue_id", venueId);
      triggerLogin();
      return;
    }

    if (!customerToken) {
      alert("Please sign in to express interest");
      return;
    }

    setInterestedLoading(true);
    try {
      if (isInterested) {
        const response = await fetch(
          `${apiUrl}/loyalty/interest/venue/${venueId}?customer_token=${encodeURIComponent(customerToken)}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to remove interest");
        }

        setIsInterested(false);
        showToast("Interest removed", "info");
      } else {
        const response = await fetch(`${apiUrl}/loyalty/interest/venue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_token: customerToken,
            venue_id: parseInt(venueId, 10),
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to express interest");
        }

        const data = await response.json();
        setIsInterested(true);
        showToast(data.message || "Thank you for your interest!", "success");
      }
    } catch (error) {
      console.error("Error toggling interest:", error);
      showToast(error.message || "Failed to update interest. Please try again.", "error");
    } finally {
      setInterestedLoading(false);
    }
  }, [isPublicMode, venueId, triggerLogin, customerToken, isInterested, apiUrl, showToast]);

  return {
    isInterested,
    interestedLoading,
    handleInterestToggle,
  };
}
